import React, { useEffect, useState } from "react";
import { Download, FileText, ShieldCheck, FileSpreadsheet, FileCheck2, Plus, Play, Pencil, Trash2, Sparkles } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { formatBytes } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CustomReportModal from "@/components/CustomReportModal";

export default function Reports() {
    const { user } = useAuth();
    const [reports, setReports] = useState([]);
    const [customs, setCustoms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [soc2Window, setSoc2Window] = useState(30);
    const [soc2Busy, setSoc2Busy] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [runningId, setRunningId] = useState(null);
    const canSoc2 = user && (user.role === "admin" || user.role === "superadmin" || user.role === "auditor");
    const canBuild = user && (user.role === "admin" || user.role === "superadmin" || user.role === "engineer");

    const refreshCustoms = async () => {
        try {
            const { data } = await http.get("/reports/custom");
            setCustoms(data);
        } catch { /* ignore */ }
    };

    useEffect(() => {
        const refresh = async () => {
            try {
                const { data } = await http.get("/reports");
                setReports(data);
            } finally {
                setLoading(false);
            }
        };
        refresh();
        refreshCustoms();
        const iv = setInterval(refresh, 8000);
        return () => clearInterval(iv);
    }, []);

    const download = async (reportId, format) => {
        const suffix = format === "xlsx" ? "download.xlsx" : "download";
        const ext = format === "xlsx" ? "xlsx" : "pdf";
        const mime = format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf";
        try {
            const res = await http.get(`/reports/${reportId}/${suffix}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `moovbytes-report-${reportId.slice(0, 8)}.${ext}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            alert("Failed to download report");
        }
    };

    const downloadSoc2 = async () => {
        setSoc2Busy(true);
        try {
            const res = await http.get(`/reports/soc2?window_days=${soc2Window}`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            const today = new Date().toISOString().slice(0, 10);
            link.setAttribute("download", `moovbytes-soc2-${today}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("SOC2 report downloaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to generate SOC2 report");
        } finally {
            setSoc2Busy(false);
        }
    };

    const runCustom = async (def, formatOverride) => {
        const fmt = formatOverride || def.format || "xlsx";
        setRunningId(def.id);
        try {
            const res = await http.get(`/reports/custom/${def.id}/run?format=${fmt}`, {
                responseType: "blob",
            });
            // Parse Content-Disposition for the server-assigned filename;
            // fall back to a sensible default so the download link doesn't
            // become "download" on browsers that ignore the header.
            const dispo = res.headers?.["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(dispo);
            const filename = m?.[1] || `${def.name.replace(/[^a-z0-9]+/gi, "_")}.${fmt}`;
            const mime = fmt === "pdf"
                ? "application/pdf"
                : fmt === "csv"
                  ? "text/csv"
                  : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Generated ${filename}`);
            refreshCustoms();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setRunningId(null);
        }
    };

    const deleteCustom = async (def) => {
        if (!window.confirm(`Delete "${def.name}"? This cannot be undone.`)) return;
        try {
            await http.delete(`/reports/custom/${def.id}`);
            toast.success(`Deleted "${def.name}"`);
            refreshCustoms();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <div className="mb-6">
                <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                    Compliance
                </div>
                <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Reports</h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    Every completed migration generates a SOC2-style compliance PDF — summary, audit trail, transfer
                    statistics, security posture, and log excerpts.
                </p>
            </div>

            {/* ── Custom Reports section ───────────────────────────── */}
            <div className="mb-6 rounded-xl border border-border bg-surface" data-testid="custom-reports-section">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3.5">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#10b981] to-[#5b5ef0] text-white">
                            <Sparkles size={16} />
                        </div>
                        <div>
                            <h3 className="font-display text-base font-bold leading-tight">Custom reports</h3>
                            <p className="text-[11px] text-muted-foreground">
                                Build a saved query against jobs, devices, alerts, audit log, or storage migrations — run anytime as PDF / XLSX / CSV.
                            </p>
                        </div>
                    </div>
                    {canBuild && (
                        <button
                            onClick={() => { setEditing(null); setModalOpen(true); }}
                            data-testid="custom-report-new-btn"
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#10b981] to-[#5b5ef0] px-3.5 py-2 text-xs font-semibold uppercase tracking-widest text-white shadow hover:brightness-110"
                        >
                            <Plus size={13} /> New custom report
                        </button>
                    )}
                </div>
                <div className="divide-y divide-border" data-testid="custom-reports-list">
                    {customs.length === 0 && (
                        <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                            No custom reports yet.
                            {canBuild && (
                                <>
                                    {" "}Build one to slice migrations by{" "}
                                    <span className="font-mono">status</span>,{" "}
                                    <span className="font-mono">agent</span>, or any other field.
                                </>
                            )}
                        </div>
                    )}
                    {customs.map((d) => (
                        <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`custom-report-row-${d.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <FileText size={14} className="text-muted-foreground" />
                                    <span className="font-display text-sm font-bold">{d.name}</span>
                                    <span className="mb-pill border-border text-muted-foreground">
                                        {d.source}
                                    </span>
                                    <span className="mb-pill border-[#5b5ef0]/30 bg-[#5b5ef0]/5 text-[#5b5ef0]">
                                        {d.format.toUpperCase()}
                                    </span>
                                </div>
                                {d.description && (
                                    <div className="mt-0.5 text-[11px] text-muted-foreground">{d.description}</div>
                                )}
                                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono text-[10px] text-muted-foreground">
                                    <span>{d.window_days}-day window</span>
                                    <span>{(d.columns || []).length} columns</span>
                                    {Object.keys(d.filters || {}).length > 0 && (
                                        <span>{Object.keys(d.filters).length} filter(s)</span>
                                    )}
                                    {d.last_run_at && (
                                        <span>last run {d.last_run_at.replace("T", " ").slice(0, 16)}{d.last_row_count != null ? ` · ${d.last_row_count} rows` : ""}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => runCustom(d)}
                                    disabled={runningId === d.id}
                                    data-testid={`custom-report-run-${d.id}`}
                                    className="inline-flex items-center gap-1 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-60"
                                >
                                    <Play size={11} /> {runningId === d.id ? "Running…" : "Run"}
                                </button>
                                {/* Per-format quick-runs */}
                                {(["pdf", "xlsx", "csv"]).filter((f) => f !== d.format).map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => runCustom(d, f)}
                                        title={`Run as ${f.toUpperCase()}`}
                                        data-testid={`custom-report-run-${d.id}-${f}`}
                                        className="rounded-md border border-border px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                                    >
                                        {f}
                                    </button>
                                ))}
                                {canBuild && (
                                    <>
                                        <button
                                            onClick={() => { setEditing(d); setModalOpen(true); }}
                                            data-testid={`custom-report-edit-${d.id}`}
                                            className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                                            title="Edit"
                                        >
                                            <Pencil size={12} />
                                        </button>
                                        <button
                                            onClick={() => deleteCustom(d)}
                                            data-testid={`custom-report-delete-${d.id}`}
                                            className="rounded-md border border-border p-1.5 text-muted-foreground hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]"
                                            title="Delete"
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {canSoc2 && (
                <div
                    className="mb-6 rounded-xl border border-[#5b5ef0]/40 bg-gradient-to-br from-[#5b5ef0]/5 to-[#7c5cff]/5 p-5"
                    data-testid="soc2-card"
                >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-[#5b5ef0] to-[#7c5cff] text-white">
                                <FileCheck2 size={18} />
                            </div>
                            <div>
                                <h3 className="font-display text-lg font-bold">Tenant-level SOC2 report</h3>
                                <p className="max-w-xl text-xs text-muted-foreground">
                                    Auditor-ready PDF: executive summary, control posture, encryption attestation,
                                    access control matrix, agent inventory, migration audit trail, and SHA-256 hash
                                    evidence of verified files.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-end gap-2">
                            <label className="block">
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                    Window
                                </div>
                                <select
                                    value={soc2Window}
                                    onChange={(e) => setSoc2Window(Number(e.target.value))}
                                    className="mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-[#5b5ef0] focus:outline-none"
                                    data-testid="soc2-window"
                                >
                                    <option value={7}>Last 7 days</option>
                                    <option value={30}>Last 30 days</option>
                                    <option value={90}>Last 90 days</option>
                                    <option value={365}>Last 365 days</option>
                                </select>
                            </label>
                            <button
                                onClick={downloadSoc2}
                                disabled={soc2Busy}
                                className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#5b5ef0]/25 transition-all hover:shadow-xl hover:shadow-[#5b5ef0]/40 disabled:opacity-60"
                                data-testid="soc2-download-btn"
                            >
                                <Download size={14} />
                                {soc2Busy ? "Generating…" : "Download SOC2 PDF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="border border-border bg-surface">
                <div className="border-b border-border px-4 py-3 flex items-center gap-2">
                    <ShieldCheck size={14} className="text-[hsl(var(--success))]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Signed by MoovBytes · AES-256 · SHA-256
                    </span>
                </div>
                <table className="w-full text-sm" data-testid="reports-table">
                    <thead className="border-b border-border">
                        <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Job status</th>
                            <th className="px-4 py-2 text-right">Files</th>
                            <th className="px-4 py-2 text-right">Size</th>
                            <th className="px-4 py-2 text-left">Generated</th>
                            <th className="px-4 py-2 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                                    Loading reports…
                                </td>
                            </tr>
                        )}
                        {!loading && reports.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                                    Reports appear automatically when jobs complete.
                                </td>
                            </tr>
                        )}
                        {reports.map((r) => (
                            <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                        <FileText size={14} className="text-muted-foreground" />
                                        <span className="font-medium">{r.name}</span>
                                    </div>
                                    <div className="font-mono text-[10px] text-muted-foreground">
                                        report_id: {r.id.slice(0, 12)}
                                    </div>
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className="mb-pill border-border text-muted-foreground">
                                        {r.job?.status || "—"}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono text-xs">
                                    {r.job?.files_done ?? "—"}
                                </td>
                                <td className="px-4 py-2.5 text-right font-mono text-xs">
                                    {formatBytes(r.job?.total_bytes || 0)}
                                </td>
                                <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                    {(r.created_at || "").replace("T", " ").slice(0, 19)}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    <div className="inline-flex items-center gap-1.5">
                                        <button
                                            onClick={() => download(r.id, "pdf")}
                                            data-testid={`report-download-${r.id}`}
                                            className="inline-flex items-center gap-1 rounded-sm border border-border px-2.5 py-1 text-xs font-semibold uppercase tracking-widest hover:border-brand hover:text-brand"
                                        >
                                            <Download size={12} /> PDF
                                        </button>
                                        <button
                                            onClick={() => download(r.id, "xlsx")}
                                            data-testid={`report-download-xlsx-${r.id}`}
                                            className="inline-flex items-center gap-1 rounded-sm border border-border px-2.5 py-1 text-xs font-semibold uppercase tracking-widest hover:border-[hsl(var(--success))] hover:text-[hsl(var(--success))]"
                                        >
                                            <FileSpreadsheet size={12} /> XLSX
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modalOpen && (
                <CustomReportModal
                    initial={editing}
                    onClose={() => { setModalOpen(false); setEditing(null); }}
                    onSaved={refreshCustoms}
                />
            )}
        </div>
    );
}
